// =============================================
// /utils/story_management.js
// Story CRUD operations
// =============================================

import { supabase } from './supabaseClient';

// Create story for existing user
export const createStoryForExistingUser = async (storyData) => {
  const { data, error } = await supabase
    .from('artspeaks_stories')
    .insert({
      vc_id: storyData.vc_id,
      artist_uuid: storyData.artist_uuid,
      title: storyData.title,
      story_text: storyData.story_text,
      inspiration_text: storyData.inspiration_text,
      process_description: storyData.process_description,
      story_type: storyData.story_type || 'inspiration',
      visibility: 'public',
      featured: storyData.featured || false,
      event_id: storyData.event_id,
      performed_at_event: storyData.performed_at_event || false,
      performance_order: storyData.performance_order,
      cover_image: storyData.cover_image,
      audio_url: storyData.audio_url,
      video_url: storyData.video_url,
      published_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select('story_id')
    .single();

  // If successful, log the activity
  if (data && !error) {
    await supabase
      .from('user_activity')
      .insert({
        uuid: storyData.artist_uuid,
        story_id: data.story_id,
        vc_id: storyData.vc_id,
        activity_type: 'created_story',
        timestamp: new Date().toISOString(),
        event_id: storyData.event_id
      });
  }

  return { data, error };
};

// Get all stories for an event (admin view)
export const getEventStoriesAdmin = async (eventId) => {
  const { data, error } = await supabase
    .from('artspeaks_stories')
    .select(`
      story_id,
      title,
      story_type,
      visibility,
      featured,
      performance_order,
      created_at,
      published_at,
      audio_url,
      video_url,
      artist:users!artist_uuid (
        uuid,
        nickname,
        handle,
        profile_img
      ),
      artwork:vcs!vc_id (
        vc_id,
        title,
        cover_image,
        creator:users!uuid (
          nickname,
          handle
        )
      )
    `)
    .eq('event_id', eventId)
    .eq('performed_at_event', true)
    .order('performance_order', { nullsLast: true })
    .order('created_at', { ascending: true });

  return { data, error };
};

// Update story performance order
export const updateStoryOrder = async (storyId, newOrder) => {
  const { data, error } = await supabase
    .from('artspeaks_stories')
    .update({ 
      performance_order: newOrder,
      updated_at: new Date().toISOString()
    })
    .eq('story_id', storyId)
    .select('story_id, performance_order')
    .single();

  return { data, error };
};

// Toggle story featured status
export const toggleStoryFeatured = async (storyId) => {
  // First get current status
  const { data: current } = await supabase
    .from('artspeaks_stories')
    .select('featured')
    .eq('story_id', storyId)
    .single();

  if (current) {
    const { data, error } = await supabase
      .from('artspeaks_stories')
      .update({ 
        featured: !current.featured,
        updated_at: new Date().toISOString()
      })
      .eq('story_id', storyId)
      .select('story_id, featured')
      .single();

    return { data, error };
  }
};

// Bulk update performance orders
export const bulkUpdatePerformanceOrder = async (updates) => {
  const promises = updates.map(update => 
    supabase
      .from('artspeaks_stories')
      .update({ 
        performance_order: update.performance_order,
        updated_at: new Date().toISOString()
      })
      .eq('story_id', update.story_id)
  );

  const results = await Promise.all(promises);
  return results;
};
